import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Icon } from "@iconify/react";
import { scanFormSchema } from "../../lib/validations";
import { WCAG_LEVELS } from "../../utils/constants";
import Input from "../common/Input";
import Select from "../common/Select";
import Button from "../common/Button";
import LoadingSpinner from "../common/LoadingSpinner";

const ScanForm = ({ onSubmit, isScanning, scanError }) => {
  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(scanFormSchema),
    defaultValues: {
      wcagLevel: "wcag2aa",
      scanType: "single",
    },
  });

  return (
    <div className="bg-white rounded-lg border border-gray-200 mb-6 overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <Icon icon="mdi:magnify-scan" className="text-gray-600 text-xl" />
          <h2 className="text-lg font-semibold text-gray-800">
            Scan Website for Accessibility Issues
          </h2>
        </div>
      </div>

      {/* Form Content */}
      <div className="p-4">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Website URL
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Icon icon="mdi:web" className="text-gray-400" />
                </div>
                <input
                  type="url"
                  placeholder="https://example.com/"
                  {...register("url")}
                  className={`w-full pl-10 pr-4 py-2.5 border rounded-lg text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary ${
                    errors.url
                      ? "border-red-300 bg-red-50"
                      : "border-gray-300 bg-white hover:border-gray-400"
                  }`}
                />
              </div>
              {errors.url && (
                <p className="mt-1 text-xs text-red-600">
                  {errors.url.message}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                WCAG Compliance Level
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Icon icon="mdi:shield-check" className="text-gray-400" />
                </div>
                <Controller
                  name="wcagLevel"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      wrapperClassName=""
                      className={`pl-10 ${
                        errors.wcagLevel
                          ? "border-red-300 bg-red-50"
                          : "border-gray-300 hover:border-gray-400"
                      }`}
                    >
                      {WCAG_LEVELS.map((level) => (
                        <option key={level.value} value={level.value}>
                          {level.label}
                        </option>
                      ))}
                    </Select>
                  )}
                />
              </div>
              {errors.wcagLevel && (
                <p className="mt-1 text-xs text-red-600">
                  {errors.wcagLevel.message}
                </p>
              )}
            </div>
          </div>

          {scanError && (
            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
              <Icon
                icon="mdi:alert-circle"
                className="text-red-500 text-lg flex-shrink-0 mt-0.5"
              />
              <p className="text-sm text-red-700">{scanError}</p>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-4 sm:items-center justify-between pt-2">
            <p className="text-xs text-gray-500">
              Scan will check for WCAG compliance issues
            </p>
            <Button
              type="submit"
              disabled={isScanning}
              className="flex items-center gap-2 px-3"
            >
              {isScanning ? (
                <>
                  <LoadingSpinner size="sm" />
                  <span>Scanning...</span>
                </>
              ) : (
                <>
                  <Icon icon="mdi:radar" />
                  <span>Start Scan</span>
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ScanForm;
